import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../firebase/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useAnalytics } from "../../firebase/hooks/useAnalytics";
import { getApiUrl } from "../../config/api";
import {
    authSearch,
    readRedirect,
    redirectTarget,
} from "../../firebase/redirect";

export default function EmailVerification() {
    const { currentUser, sendVerificationEmail, reloadUser } = useAuth();
    const [resendCooldown, setResendCooldown] = useState(0);
    const [message, setMessage] = useState("");
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { trackPageView } = useAnalytics();

    // Helper to get language from URL
    const getLangFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        return params.get("lang") === "zh" ? "zh" : "en";
    }, [location.search]);

    const [language, setLanguage] = useState(getLangFromUrl);
    const isEN = language === "en";

    // Track page view when component mounts
    useEffect(() => {
        trackPageView("Email Verification");
    }, [trackPageView]);

    // Update language when URL changes
    useEffect(() => {
        setLanguage(getLangFromUrl());
    }, [getLangFromUrl]);

    // Update URL when language changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("lang") !== language) {
            params.set("lang", language);
            navigate({ search: params.toString() }, { replace: true });
        }
    }, [language, navigate, location.search]);

    // Where this visitor was headed before the auth detour, forwarded here by
    // ProtectedRoute or by the login form.
    const from = readRedirect(location.search);

    // Redirect if no user or already verified
    useEffect(() => {
        if (!currentUser) {
            navigate(`/login${authSearch(language, from)}`, { replace: true });
        } else if (currentUser.emailVerified) {
            navigate(redirectTarget(location.search, language), {
                replace: true,
            });
        }
    }, [currentUser, navigate, language, from, location.search]);

    // Resend cooldown timer
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(
                () => setResendCooldown(resendCooldown - 1),
                1000,
            );
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleResendEmail = async () => {
        try {
            await sendVerificationEmail();
            setMessage(
                isEN
                    ? "Verification email sent! Please check your inbox."
                    : "驗證郵件已發送！請檢查您的收件匣。",
            );
            setResendCooldown(60); // 60 second cooldown
        } catch (error) {
            setMessage(
                isEN
                    ? "Failed to send verification email. Please try again."
                    : "發送驗證郵件失敗。請重試。",
            );
        }
    };

    const handleCheckEmailVerification = async () => {
        setIsCheckingEmail(true);
        try {
            await reloadUser();
            if (currentUser?.emailVerified) {
                const token = await currentUser.getIdToken(true);

                const res = await fetch(`${getApiUrl()}/api/initialize-user`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });

                if (!res.ok) throw new Error("Failed to initialize user data.");

                await currentUser.getIdToken(true);

                navigate(redirectTarget(location.search, language), {
                    replace: true,
                });
            } else {
                setMessage(
                    isEN
                        ? "Email not yet verified. Please check your email and click the verification link."
                        : "電子郵件尚未驗證。請檢查您的電子郵件並點擊驗證連結。",
                );
            }
        } catch (error) {
            setMessage(
                isEN
                    ? "Failed to check verification status. Please try again."
                    : "檢查驗證狀態失敗。請重試。",
            );
        }
        setIsCheckingEmail(false);
    };

    if (!currentUser) {
        return null; // Will redirect in useEffect
    }

    return (
        <div className="flex h-screen bg-gray-50 font-sans">
            <div className="flex-1 flex justify-center items-center">
                <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-md">
                    {/* Language Switcher */}
                    <div className="flex justify-end mb-6">
                        <button
                            onClick={() => setLanguage("en")}
                            className={`text-sm px-3 py-1 rounded-l transition-all duration-200 ${
                                language === "en"
                                    ? "bg-orange-600 text-white"
                                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                            }`}
                        >
                            EN
                        </button>
                        <button
                            onClick={() => setLanguage("zh")}
                            className={`text-sm px-3 py-1 rounded-r transition-all duration-200 ${
                                language === "zh"
                                    ? "bg-orange-600 text-white"
                                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                            }`}
                        >
                            中文
                        </button>
                    </div>

                    {/* Logo */}
                    <div className="text-center mb-8">
                        <img
                            src="/logo_tglobal.png"
                            alt="T-Global"
                            className="w-32 h-auto mx-auto mb-4"
                        />
                    </div>

                    {/* Email Verification Content */}
                    <div className="text-center">
                        <div className="mb-6">
                            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg
                                    className="w-8 h-8 text-orange-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    ></path>
                                </svg>
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                {isEN
                                    ? "Verify Your Email"
                                    : "驗證您的電子郵件"}
                            </h1>
                            <p className="text-gray-600 mb-2">
                                {isEN
                                    ? "We've sent a verification email to:"
                                    : "我們已向以下地址發送驗證郵件："}
                            </p>
                            <p className="text-sm font-medium text-orange-600 mb-4">
                                {currentUser?.email}
                            </p>
                            <p className="text-sm text-gray-500">
                                {isEN
                                    ? "Please check your email and click the verification link to continue."
                                    : "請檢查您的電子郵件並點擊驗證連結以繼續。"}
                            </p>
                        </div>

                        {message && (
                            <div
                                className={`p-3 rounded-md mb-4 text-sm ${
                                    message.includes("Failed") ||
                                    message.includes("失敗")
                                        ? "bg-red-50 text-red-700 border border-red-200"
                                        : "bg-green-50 text-green-700 border border-green-200"
                                }`}
                            >
                                {message}
                            </div>
                        )}

                        <div className="space-y-3">
                            <button
                                onClick={handleCheckEmailVerification}
                                disabled={isCheckingEmail}
                                className="w-full py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white font-medium rounded-md transition-colors duration-200"
                            >
                                {isCheckingEmail
                                    ? isEN
                                        ? "Checking..."
                                        : "檢查中..."
                                    : isEN
                                      ? "I've Verified My Email"
                                      : "我已驗證我的電子郵件"}
                            </button>

                            <button
                                onClick={handleResendEmail}
                                disabled={resendCooldown > 0}
                                className="w-full py-3 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 font-medium rounded-md transition-colors duration-200"
                            >
                                {resendCooldown > 0
                                    ? isEN
                                        ? `Resend in ${resendCooldown}s`
                                        : `${resendCooldown}秒後重發`
                                    : isEN
                                      ? "Resend Verification Email"
                                      : "重新發送驗證郵件"}
                            </button>

                            <button
                                onClick={() =>
                                    navigate(
                                        `/login${authSearch(language, from)}`,
                                    )
                                }
                                className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors duration-200"
                            >
                                {isEN ? "Back to Login" : "返回登入"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
